<template>
  <div>
    <!--<ReportSubLinks />-->
    <AccountingSummaryFilter
      id="accounting-summary-filter"
      :month="filter.month"
      @update:month="filter.month = $event"
      :activityType="filter.activityType"
      @update:activityType="filter.activityType = $event"
    />

    <div v-if="isFetching" class="mt-10">
      <h2 class="font-heading font-semibold">Loading Summary...</h2>
    </div>
    <table v-else-if="accountingSummary" class="max-w-screen-sm w-full mt-10">
      <tbody>
        <!--Total Sales-->
        <tr id="ticket-sales">
          <td class="w-3/4">Ticket Sales</td>
          <td class="text-right">{{ currency(accountingSummary.face) }}</td>
        </tr>
        <tr id="add-on-sales">
          <td class="w-3/4">Add-on Sales</td>
          <td class="text-right">{{ currency(accountingSummary.add_ons) }}</td>
        </tr>
        <tr id="discounts">
          <td class="w-3/4">Discounts</td>
          <td class="text-right">-{{ currency(accountingSummary.discounts) }}</td>
        </tr>
        <tr id="refunds">
          <td class="w-3/4">Refunds</td>
          <td class="text-right">-{{ currency(accountingSummary.refunds) }}</td>
        </tr>
        <tr id="venue-processing-fees">
          <td class="pb-2 w-3/4">Venue Processing Fees ({{ venueProcessingFees }})</td>
          <td class="pb-2 text-right">{{ currency(accountingSummary.service_fee) }}</td>
        </tr>
        <tr class="border-t border-linear-subtle" id="total-sales">
          <td class="pt-2 w-3/4"><span class="font-bold">Total Sales</span></td>
          <td class="pt-2 text-right">
            <span class="font-bold">{{ currency(totalSales) }}</span>
          </td>
        </tr>

        <!--Total Tax-->
        <!--<tr id="ticket-sales-tax">
          <td class="pt-8 w-3/4">Ticket Sales Tax</td>
          <td class="pt-8 text-right">{{ currency(accountingSummary.ticket_tax) }}</td>
        </tr>-->
        <!--<tr id="add-on-sales-tax">
          <td class="pb-2 w-3/4">Add-on Sales Tax</td>
          <td class="pb-2 text-right">{{ currency(accountingSummary.add_ons_tax) }}</td>
        </tr>-->
        <tr class="border-t border-linear-subtle" id="total-tax">
          <td class="pt-2 w-3/4"><span class="font-bold">Total Sales Tax</span></td>
          <td class="pt-2 text-right">
            <span class="font-bold">{{ currency(totalTax) }}</span>
          </td>
        </tr>

        <!--Gross Sales-->
        <tr>
          <td class="pt-8"></td>
          <td class="pt-8"></td>
        </tr>
        <tr class="border-t border-linear-subtle" id="gross-sales">
          <td class="pt-2 w-3/4"><span class="font-bold">Gross Sales</span></td>
          <td class="pt-2 text-right">
            <span class="font-bold">{{ currency(grossSales) }}</span>
          </td>
        </tr>

        <!--Total Fees-->
        <tr id="stripe-fees">
          <td class="pt-8 w-3/4">Stripe Fees</td>
          <td class="pt-8 text-right">-{{ currency(accountingSummary.stripe_fees) }}</td>
        </tr>
        <tr id="turntable-fees">
          <td class="pb-2 w-3/4">Turntable Application Fees</td>
          <td class="pb-2 text-right">-{{ currency(accountingSummary.application_fees) }}</td>
        </tr>
        <tr class="border-t border-linear-subtle" id="total-fees">
          <td class="pt-2 w-3/4"><span class="font-bold">Total Fees</span></td>
          <td class="pt-2 text-right">
            <span class="font-bold">-{{ currency(totalFees) }}</span>
          </td>
        </tr>

        <!--Net Revenue-->
        <tr>
          <td class="pt-8"></td>
          <td class="pt-8"></td>
        </tr>
        <tr class="border-t border-linear-subtle" id="net-revenue">
          <td class="pt-2 w-3/4"><span class="font-bold">Net Revenue</span></td>
          <td class="pt-2 text-right">
            <span class="font-bold">{{ currency(netRevenue) }}</span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="mt-10">
      <h2 class="font-heading font-semibold">No Summary For This Month</h2>
    </div>
  </div>
</template>

<script lang="ts">
import moment from 'moment'
import { mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import AccountingSummaryFilter from '@/components/AccountingSummaryFilter.vue'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useSettingStore } from '@/stores/setting'
import { blankError, displayHandled } from '@/utils/errors'

import { AccountingReportRead } from '@generated/types'

type ActivityType = 'cash' | 'accrual'

export default defineComponent({
  name: 'AccountingSummary',
  components: { AccountingSummaryFilter },
  data() {
    return {
      filter: {
        month: moment().format('YYYY-MM'),
        activityType: 'cash' as ActivityType,
      },
      isFetching: true,
      accountingSummary: null as AccountingReportRead | null,
      error: blankError(),
    }
  },
  async mounted() {
    await this.settingStore.loading
    await this.fetchSummary()
  },
  methods: {
    async fetchSummary(): Promise<void> {
      try {
        this.isFetching = true
        this.accountingSummary = null
        const data = await this.performanceStore.getAccountingSummary(
          this.filter.month,
          this.filter.activityType,
        )
        this.accountingSummary = data[0]
      } catch (error) {
        displayHandled(error, this.error)
      } finally {
        this.isFetching = false
      }
    },
  },
  computed: {
    ...mapStores(usePerformanceStore, useSettingStore),
    ...mapState(useSettingStore, ['currency']),
    totalSales(): number {
      if (!this.accountingSummary) return 0
      return (
        +this.accountingSummary.face +
        +this.accountingSummary.add_ons -
        +this.accountingSummary.discounts -
        +this.accountingSummary.refunds +
        +this.accountingSummary.service_fee
      )
    },
    totalTax(): number {
      if (!this.accountingSummary) return 0
      return +this.accountingSummary.ticket_tax /*+ +this.accountingSummary.add_ons_tax*/
    },
    grossSales(): number {
      return this.totalSales + this.totalTax
    },
    totalFees(): number {
      if (!this.accountingSummary) return 0
      return +this.accountingSummary.stripe_fees + +this.accountingSummary.application_fees
    },
    netRevenue(): number {
      return this.grossSales - this.totalFees
    },
    venueProcessingFees(): string {
      return `${this.settingStore.currency(this.settingStore.service_fee)} +
        ${this.settingStore.service_percentage}%`
    },
  },
  watch: {
    filter: {
      handler: 'fetchSummary',
      deep: true,
    },
  },
})
</script>
